import { useQuery } from '@apollo/client';
import { useAuth } from '@/services/auth/AuthProvider';
import { GET_ADMIN_ACCESS } from '@/shared/queries/admin';
import { QueryResult } from '@/shared/types/generated';
import { IS_WEB } from '@/shared/constants/utility';

/**
 * Whether the signed-in user can use the admin dashboard. The server decides
 * (verified Auth0 email on an allowlist) and enforces it on every admin query;
 * this only controls what the web app shows.
 */
export function useIsAdmin() {
  const { isAuthenticated, loadingAuth } = useAuth();
  // The dashboard is web-only, so the iOS app never needs to ask
  const { data, loading, error, refetch } = useQuery<
    QueryResult<'adminAccess'>
  >(GET_ADMIN_ACCESS, {
    skip: !isAuthenticated || !IS_WEB,
    // Ask again on each visit so an earlier answer never sticks for the session
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  return {
    isAdmin: data?.adminAccess === true,
    // Until sign-in is restored the query is skipped, which isn't a "no" yet;
    // without this a refresh on /admin bounced admins to Sessions
    isLoading: loadingAuth || loading,
    // The check itself failed (e.g. Auth0 blip), which isn't a "no"
    error,
    refetch,
  };
}
